<template>
    <v-layout align-center justify-center class="upload_bg">

        <!-- GLOBAL APP BAR HEADER -->
        <v-toolbar fixed flat class="transparent white--text">
            <span v-if="!isMobile">Update Profile Avatar</span>
            <v-spacer></v-spacer>
           
            <!-- WORKFLOW TOGGLE KEYS -->
            <v-toolbar-items>    
                <v-btn dark large flat @click="turnOnVideo" class="mr-3 mt-3" v-show="showCameraButton">
                    <v-icon class="mr-10">photo_camera</v-icon>Use Camera
                </v-btn>
                <v-btn dark large flat @click="turnOffVideo" class="mr-3" v-show="showUploadButton">
                    <v-icon class="mr-10">cloud_upload</v-icon>Upload File
                </v-btn>   
                <v-btn dark flat @click="closeDialog">Cancel</v-btn>
            </v-toolbar-items>
        </v-toolbar>

         <v-alert v-if="error" :value="true" type="error" icon="error" dismissible class="mb-4">
            {{ errorMessage }}
        </v-alert>
        
        <!-- CONTAINER CONTENT BOX -->
        <v-flex xs12 sm7 :class="isMobile ? 'content-container-mobile' : ''"> 

            <div class="text-xs-center" :class="isMobile ? 'content-container-main-mobile' : ''">
                <!-- LIVE CAMERA VIEWFINDER -->
                <div v-show="showCameraScreen" class="camera-frame mb-3">
                    <video autoplay playsinline muted class="video-image"></video>
                </div>

                <!-- CAPTURED SNAPSHOT CANVAS INTERFACE -->
                <div v-show="showImage" class="camera-frame mb-3">
                    <img id="imgMyProfile" src="" class="video-image">
                </div>

                <!-- DRAG AND DROP FILE COMPONENT LAYER -->
                <div v-show="showUpload" class="text-xs-center">
                    <vue-dropzone ref="myVueDropzone" 
                        class="dropzone-component"
                        id="dropzone" 
                        :options="dropzoneOptions"
                        v-on:vdropzone-success="uploadSuccess"
                        v-on:vdropzone-file-added="fileAdded"
                    ></vue-dropzone>
                </div>
            </div>

            <!-- DYNAMIC CONTROL ACTIONS BAR CONTAINER -->
            <div :class="isMobile ? 'img-controls' : 'text-xs-center mt-4'">
                <v-btn dark large @click="takeScreenshot" :loading="loadingScreenshot" v-show="showScreenshot" color="primary" title="Take Picture">
                    <v-icon class="mr-2">linked_camera</v-icon>Take Picture
                </v-btn>
                <v-btn dark large @click="takeAgain" v-show="showSavePic" outline class="mr-2" title="Take Another">
                    <v-icon class="mr-2">refresh</v-icon>Take Again
                </v-btn>
                <v-btn dark large @click="savePicture" :loading="uploading" v-show="showSavePic" color="primary" title="Save Profile Picture">
                    <v-icon class="mr-2">save</v-icon>Save Avatar
                </v-btn>
            </div>
        </v-flex> 
        
    </v-layout>
</template>

<script>
import axios from 'axios'
import uploadApi from '@/api/upload.js'
import vue2Dropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'

export default {
    name: 'EditAvatar',
    components: {
        vueDropzone: vue2Dropzone
    },
    data () {
        return {
            userId: 0,
            loading: false,   
            video: null,
            videoStream: null,
            img: null,
            
            // View state visibility triggers synced with UploadPhoto
            showCameraButton: true,       
            showUploadButton: false,
            showCameraScreen: false,
            showScreenshot: false,
            showImage: false,
            showSavePic: false,
            showUpload: true,      
            loadingScreenshot: false,            
            uploading: false,
            error: false,
            errorMessage: '', 
        }
    },
    methods: {
        syncAvatar(imageBase64) {
            this.$store.commit('setAvatar', imageBase64)
            this.$root.$emit('updateUserAvatar', 'updateUserAvatarHandler')
        },
        // --- LIVE HARDWARE STREAM CONTROLS ---
        turnOnVideo() {
            try {
                this.error = false
                this.errorMessage = ''
                this.showCameraScreen = true
                this.showCameraButton = false
                this.showUploadButton = true
                this.showUpload = false
                this.showScreenshot = true
                this.loadingScreenshot = true
                this.showImage = false
                this.showSavePic = false

                const constraints = {
                    video: { width: { exact: 440 }, height: { exact: 360 } }
                };
                this.video = document.querySelector('video');

                navigator.mediaDevices.getUserMedia(constraints)
                    .then((stream) => {
                        this.video.srcObject = stream
                        this.videoStream = stream
                        this.loadingScreenshot = false
                    })
                    .catch((err) => {
                        this.loadingScreenshot = false
                        this.error = true
                        this.errorMessage = "Could not open camera stream. Please check app permissions."
                        this.turnOffVideo()
                    });
            } catch(error) {
                this.error = true
                this.errorMessage = error
            }
        },
        turnOffVideo() {
            this.turnOffDevice()
            this.showCameraScreen = false
            this.showScreenshot = false
            this.showUpload = true
            this.showImage = false
            this.showSavePic = false
            this.showCameraButton = true
            this.showUploadButton = false
        },
        turnOffDevice() {
            if (this.videoStream !== null) {
                this.videoStream.getTracks().forEach((track) => {
                    track.stop()
                });
                this.videoStream = null
            }
        },
        takeAgain() {
            this.showImage = false
            this.showSavePic = false
            this.turnOffDevice()
            this.turnOnVideo()
        },
        // --- MATCH VIEWPORT CHANNELS EXACTLY (FREEZE FRAME EFFECT) ---
        async takeScreenshot() {
            try {
                this.showImage = true;
                this.img = document.getElementById('imgMyProfile');
                
                const canvas = document.createElement('canvas');
                
                // 1. Read the precise native feed width/height from the camera track
                const nativeWidth = this.video.videoWidth;
                const nativeHeight = this.video.videoHeight;
                
                // 2. Map canvas boundaries 1:1 to prevent distortion or clipping
                canvas.width = nativeWidth;
                canvas.height = nativeHeight;
                
                const ctx = canvas.getContext('2d');

                // 3. Draw the full, un-cropped video viewport snapshot
                ctx.drawImage(this.video, 0, 0, nativeWidth, nativeHeight);
            
                // 4. Output image matching original configuration targets (JPEG 95% for avatars)
                this.img.src = canvas.toDataURL('image/jpeg', 0.95);

                this.showCameraScreen = false;
                this.showScreenshot = false;
                this.showSavePic = true;
                this.turnOffDevice();
            } catch (error) {
                this.error = true;
                this.errorMessage = "Failed to capture freeze-frame display snapshot.";
            }
        },
        async savePicture() {
            try {
                this.uploading = true;
                this.error = false;
                this.errorMessage = '';
                
                const imageBase64 = this.img.src;
                const data = {
                    imageData: imageBase64
                };

                const response = await uploadApi.uploadAvatarBase64String(data);
                if (response && response.status === 200) {
                    this.syncAvatar(imageBase64);
                    this.$emit('send', null);
                    this.uploading = false;
                    this.closeDialog();
                } else {
                    throw new Error("Failed to upload updated profile avatar configuration.");
                }
            } catch (error) {
                this.uploading = false;
                this.error = true;
                this.errorMessage = error.message || error;
            }
        },
        // --- FILE DROPZONE ACTION INTEGRATIONS ---
        fileAdded(file) {
            this.loading = true
            this.error = false
        },
        async uploadSuccess(file, response) {
            try {
                this.loading = true
                this.error = false

                const imageBase64 = file.dataURL
                const data = {
                    imageData: imageBase64
                }

                const uploadResponse = await uploadApi.uploadAvatarBase64String(data)
                if (uploadResponse && uploadResponse.status === 200) {
                    this.$refs.myVueDropzone.removeAllFiles()
                    this.syncAvatar(imageBase64)
                    this.$emit('send', null)
                } else {
                    throw new Error("Failed to save uploaded dropzone picture asset.")
                }

                this.loading = false
                this.closeDialog()
            } catch (error) {
                this.loading = false
                this.error = true
                this.errorMessage = error.message || error
            }
        },
        closeDialog() {
            this.turnOffDevice()
            this.showUpload = true
            this.showCameraScreen = false
            this.showCameraButton = true
            this.showUploadButton = false
            this.showScreenshot = false
            this.showImage = false
            this.showSavePic = false
            this.error = false
            this.errorMessage = ''
            
            this.$emit('update:dialogEditAvatar', false)     
        }
    },        
    created() {
        this.userId = this.$store.state.userId
    },
    computed: {
        dropzoneOptions() {    
            return {   
                url: `${axios.defaults.baseURL}Image/upload/avatar`,    
                headers: { 
                    "Authorization": `Bearer ${localStorage.token}`,
                    "session_id": localStorage.sessionId,
                    "source": "Customer"
                },
                addRemoveLinks: false, 
                
                dictInvalidFileType: "Unsupported Format. Please select a valid profile image.",
                dictFileTooBig: "File size exceeds 5MB limit.",
                dictMaxFilesExceeded: "You can only upload one photo at a time.",
                dictDefaultMessage: this.dropDescription,
                maxFiles: 1,
                acceptedFiles: 'image/*', 
                maxFilesize: 5.0, //5MB

                init: function() {
                   this.on("error", function(file, message) {
                       const errorEl = file.previewElement.querySelector('.dz-error-message');
                       if (errorEl) {
                           errorEl.addEventListener('click', () => {
                               this.removeFile(file);
                           });
                       }
                   });
                }
            }
        },
        dropDescription() {
            return `<p class='subheading'><u>${this.isMobile ? 'Tap to browse gallery' : 'Drop your photo here or click to browse gallery'}</u></p><br>
                    <p class='subheading'>Maximum 5 MB</p>`
        }
    },
    destroyed() {
        this.turnOffDevice()
    }
}
</script>

<style scoped>
.upload_bg {
    height: 100%;
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    background-color: rgba(0, 0, 0, 0.9)
}
.content-container-mobile {
    position: relative !important;
    height: 100% !important;
}
.content-container-main-mobile {
    position: absolute; 
    top: 20%; 
    width: 100%;
}
.dropzone-component {
    height: 300px !important;
}
.camera-frame {
    max-width: 100%;
    margin: 0 auto;
    display: flex;
    justify-content: center;
    align-items: center;
}
.video-image {
    max-width: 100% !important;
    width: auto !important;
    height: auto !important;
    border-radius: 8px !important;
    margin: auto;
}
.img-controls {
    position: absolute; 
    bottom: 5%; 
    left: 50%; 
    transform: translateX(-50%); 
    width: 90%; 
    padding: 10px 10px 20px 10px;
    box-sizing: border-box;
    z-index: 99 !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 10px !important;
}

/* ==========================================================================
   DROPZONE VUE-DEEP STYLING OVERRIDES (CENTERED TEXT ONLY MINIMALIST LOOK)
   ========================================================================== */
::v-deep #dropzone {
    border: 2px dashed rgba(255, 255, 255, 0.3) !important;
    border-radius: 8px;
    background-color: rgba(0, 0, 0, 0.4) !important;
    color: white !important;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    margin: 10px;
}
::v-deep #dropzone .dz-preview {
    background: #1e1e1e !important; 
    border: 1px solid rgba(255, 255, 255, 0.15) !important;
    border-radius: 8px !important;
    padding: 20px !important;
    width: 280px !important; 
    position: absolute !important;
    left: 50% !important;
    top: 50% !important;
    transform: translate(-50%, -50%) !important;
    margin: 0 !important;
    box-sizing: border-box !important;
    box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.5) !important;
}
::v-deep #dropzone .dz-preview .dz-image {
    border-radius: 6px !important;
    margin: 0 auto 15px auto !important;
    display: block !important;
    width: 120px !important;
    height: 120px !important;
}
::v-deep #dropzone .dz-preview.dz-error .dz-image {
    display: none !important;
}
::v-deep #dropzone .dz-preview .dz-details {
    color: #FFF !important;
    background: transparent !important;
    opacity: 1 !important;
    position: static !important;
    padding: 0 !important;
    text-align: center !important;
    margin-bottom: 12px !important;
}
::v-deep #dropzone .dz-preview .dz-error-message {
    display: none !important; 
}
::v-deep #dropzone .dz-preview.dz-error .dz-error-message {
    position: static !important; 
    opacity: 1 !important;
    display: block !important;
    background: #FF5252 !important;
    color: white !important;
    border-radius: 4px !important;
    padding: 12px 16px !important;
    font-size: 13px !important;
    font-weight: bold !important;
    text-align: center !important;
    margin: 5px 0 0 0 !important;
    width: 100% !important;
    box-sizing: border-box !important;
    cursor: pointer !important;
    transition: background-color 0.2s ease;
}
::v-deep #dropzone .dz-preview.dz-error .dz-error-message:hover {
    background-color: #D32F2F !important;
}
::v-deep #dropzone .dz-preview .dz-error-message:after {
    display: none !important;
}
::v-deep #dropzone .dz-preview .dz-success-mark,
::v-deep #dropzone .dz-preview .dz-error-mark,
::v-deep #dropzone .dz-preview .dz-remove {
    display: none !important;
}
</style>
